import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTheme } from 'next-themes'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Sun, Moon, Monitor, Loader2 } from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { AccountSecurityCard } from './profile/AccountSecurityCard'

type UserSettings = {
  email_notifications: boolean
  push_notifications: boolean
  post_published_notify: boolean
  new_comments_notify: boolean
  weekly_reports_notify: boolean
  profile_visibility: boolean
  analytics_sharing: boolean
}

type UserPreferences = {
  language: string
  date_format: string
  time_format: string
}

const DEFAULT_SETTINGS: UserSettings = {
  email_notifications: true, push_notifications: true, post_published_notify: true,
  new_comments_notify: true, weekly_reports_notify: false,
  profile_visibility: true, analytics_sharing: false,
}
const DEFAULT_PREFS: UserPreferences = { language: 'en', date_format: 'DD/MM/YYYY', time_format: '24h' }

function Row({ label, description, checked, onCheckedChange }: { label: string; description: string; checked: boolean; onCheckedChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5 shrink-0" />
    </div>
  )
}

export function PortalSettingsView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const { theme, setTheme } = useTheme()
  const [userId, setUserId] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)

  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS)
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFS)
  const [savingSettings, setSavingSettings] = useState(false)
  const [savingPrefs, setSavingPrefs] = useState(false)

  const { data } = useQuery({
    queryKey: ['portal-settings'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      setUserId(user.id)
      setEmail(user.email ?? null)
      const [{ data: s }, { data: p }] = await Promise.all([
        supabase.from('user_settings' as any).select('*').eq('user_id', user.id).maybeSingle() as any,
        supabase.from('user_preferences' as any).select('*').eq('user_id', user.id).maybeSingle() as any,
      ])
      return { settings: s as UserSettings | null, prefs: p as UserPreferences | null }
    },
  })

  useEffect(() => {
    if (data?.settings) setSettings({ ...DEFAULT_SETTINGS, ...data.settings })
    if (data?.prefs) setPrefs({ ...DEFAULT_PREFS, ...data.prefs })
  }, [data])

  const saveSettings = async (next: UserSettings) => {
    if (!userId) return
    setSettings(next)
    setSavingSettings(true)
    try {
      const { error } = await (supabase.from('user_settings' as any).upsert({ user_id: userId, ...next }, { onConflict: 'user_id' }) as any)
      if (error) throw error
      qc.invalidateQueries({ queryKey: ['portal-settings'] })
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err.message, variant: 'destructive' })
    } finally {
      setSavingSettings(false)
    }
  }

  const savePrefs = async (next: UserPreferences) => {
    if (!userId) return
    setPrefs(next)
    setSavingPrefs(true)
    try {
      const { error } = await (supabase.from('user_preferences' as any).upsert({ user_id: userId, ...next }, { onConflict: 'user_id' }) as any)
      if (error) throw error
      qc.invalidateQueries({ queryKey: ['portal-settings'] })
    } catch (err: any) {
      toast({ title: 'Failed to save', description: err.message, variant: 'destructive' })
    } finally {
      setSavingPrefs(false)
    }
  }

  const globalSignOut = async () => {
    if (!confirm('Sign out of this account on every device? You will need to sign in again everywhere.')) return
    await supabase.auth.signOut({ scope: 'global' } as any)
    window.location.href = '/portal/login'
  }

  return (
    <div className="space-y-6">
      <PortalPageHeader title="Settings" description="Manage your account, notifications, and preferences." />

      <AccountSecurityCard email={email} />

      <Card>
        <CardHeader><CardTitle className="text-base">Notifications</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          <Row label="Email notifications" description="Receive updates by email." checked={settings.email_notifications} onCheckedChange={v => saveSettings({ ...settings, email_notifications: v })} />
          <Row label="Push notifications" description="Receive updates in your browser." checked={settings.push_notifications} onCheckedChange={v => saveSettings({ ...settings, push_notifications: v })} />
          <Row label="Content published" description="Notify when your submitted content is published." checked={settings.post_published_notify} onCheckedChange={v => saveSettings({ ...settings, post_published_notify: v })} />
          <Row label="New comments" description="Notify on new comments related to your activity." checked={settings.new_comments_notify} onCheckedChange={v => saveSettings({ ...settings, new_comments_notify: v })} />
          <Row label="Weekly reports" description="A weekly summary of your coverage and accreditation activity." checked={settings.weekly_reports_notify} onCheckedChange={v => saveSettings({ ...settings, weekly_reports_notify: v })} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Appearance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Theme</p>
            <div className="flex gap-2">
              {[
                { value: 'light', icon: Sun, label: 'Light' },
                { value: 'dark', icon: Moon, label: 'Dark' },
                { value: 'system', icon: Monitor, label: 'System' },
              ].map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-lg border text-xs transition-colors ${
                    theme === value ? 'border-accent bg-accent/5 text-accent' : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Language</p>
            <Select value={prefs.language} onValueChange={v => savePrefs({ ...prefs, language: v })}>
              <SelectTrigger className="w-full sm:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="pt">Português</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Privacy</CardTitle></CardHeader>
        <CardContent className="divide-y divide-border">
          <Row
            label="Profile visibility"
            description="Show your public journalist profile in searches when it's published."
            checked={settings.profile_visibility}
            onCheckedChange={v => saveSettings({ ...settings, profile_visibility: v })}
          />
          <Row
            label="Analytics sharing"
            description="Let accrediting institutions see aggregate coverage analytics tied to your submissions."
            checked={settings.analytics_sharing}
            onCheckedChange={v => saveSettings({ ...settings, analytics_sharing: v })}
          />
        </CardContent>
      </Card>

      <Card className="border-destructive/30">
        <CardHeader><CardTitle className="text-base text-destructive">Danger zone</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Sign out everywhere</p>
              <p className="text-xs text-muted-foreground mt-0.5">End your session on every device you're signed in on.</p>
            </div>
            <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 shrink-0" onClick={globalSignOut}>
              Sign out everywhere
            </Button>
          </div>
        </CardContent>
      </Card>

      {(savingSettings || savingPrefs) && (
        <div className="fixed bottom-4 right-4 bg-card border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground flex items-center gap-2 shadow-lg">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
        </div>
      )}
    </div>
  )
}
